import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { Settings } from "../../../settings";
import {
  selectBet,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
  selectFreeSpinsActive,
  selectFreeSpinsCurrentRound,
  selectFreeSpinsTriggerScatterCell,
  selectFreeSpinsPackageTier,
  selectFreeSpinsAwarded,
  selectFreeSpinsSpinning,
} from "../../../State/selectors";
import FreeSpinBasicOverlayReelController from "./FreeSpinBasicOverlayReelController";
import { safeInit } from "../../../utilities";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { goldenFinalBoard } from "../../Shared/goldenMysteryStaging";

const FreeSpinBasicOverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, symbolRefs }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const reelContainerRef = useRef(null);
    // Latest bet, readable from the mount effect's stale closure. The
    // controller can be built a frame or more after mount (see the readiness
    // retry below), and the [bet] effect below bails out while the controller
    // is still null — so without this a deferred build would freeze the
    // mount-time bet into every coin value text.
    const betRef = useRef(null);
    // See where this is set — it exists purely so effects can depend on the
    // controller actually being there.
    const [controllerReady, setControllerReady] = useState(false);
    
    // Use free rounds bet when active, otherwise use regular bet - memoized
    const freeRoundsActive = useSelector(selectFreeRoundsActive);
    const freeRoundsBet = useSelector(selectFreeRoundsBet);
    const regularBet = useSelector(selectBet);
    const bet = useMemo(
      () =>
        FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && freeRoundsBet
          ? freeRoundsBet
          : regularBet,
      [freeRoundsActive, freeRoundsBet, regularBet]
    );

    // ── The won package, standing on the board it was won on ──
    //
    // Only while the room is showing the board it opened with. The first free
    // spin replaces that board — scatters and all — so the card comes off with
    // it and this effect does the removing, keyed on the same flag.
    //
    // The TIER is read now, not stored at the trigger: the gamble ladder is
    // still open when the cell is recorded, and climbing it changes the
    // package. `awarded` is the spin count the round is actually being played
    // with, which is the number printed on the card.
    // In the deps for one reason: the mount above happens while the intro
    // popup is still up and bonus_game_view sits at alpha 0.001, so the cell
    // is put together entirely off-screen. Nothing else changes at the moment
    // the room actually takes the screen, so without this the effect never
    // runs again and whatever happened in between is never undone.
    const freeSpinsRoomActive = useSelector(selectFreeSpinsActive);
    const freeSpinsRound = useSelector(selectFreeSpinsCurrentRound);
    const triggerScatterCell = useSelector(selectFreeSpinsTriggerScatterCell);
    const packageTier = useSelector(selectFreeSpinsPackageTier);
    const awardedSpins = useSelector(selectFreeSpinsAwarded);
    // ── The card comes off when the SPIN starts, not when the reply lands ──
    //
    // Without this the only thing that took it down was `isTriggerBoard` going
    // false, and that is the server's currentRound replacing the arrival board
    // — which happens long after the reels are already turning. So the card
    // stood on a spinning reel and then vanished mid-flight, unlike every other
    // overlay symbol, which are swept before the board moves.
    //
    // freeSpins.spinning is raised by handleFreeSpin BEFORE startReels, so this
    // effect re-runs and releases the cell while the board is still at rest.
    const freeSpinsSpinning = useSelector(selectFreeSpinsSpinning);

    useEffect(() => {
      const controller = overlayReelControllerRef.current;
      if (!controller) return undefined;

      const onTriggerBoard = freeSpinsRound?.isTriggerBoard === true;
      const cellReel = Array.isArray(triggerScatterCell)
        ? triggerScatterCell[0]
        : null;
      const cellRow = Array.isArray(triggerScatterCell)
        ? triggerScatterCell[1]
        : null;

      if (
        freeSpinsSpinning ||
        !onTriggerBoard ||
        cellReel !== index ||
        typeof cellRow !== "number" ||
        typeof packageTier !== "number"
      ) {
        controller.hideSettledGambleScatter?.();
        return undefined;
      }

      controller.showSettledGambleScatter?.(cellRow, {
        tier: packageTier,
        value: awardedSpins,
      });

      return () => controller.hideSettledGambleScatter?.();
    }, [
      index,
      controllerReady,
      freeSpinsRoomActive,
      freeSpinsSpinning,
      freeSpinsRound,
      triggerScatterCell,
      packageTier,
      awardedSpins,
    ]);

    // ── The Golden cells the room ARRIVED with are scenery ──
    //
    // FreeSpinBasicReel paints them as flat composites on the arrival board —
    // the coin with its value, the magnet with its total, exactly the picture
    // the base game finished on. They are not ours to present: that spin was
    // played, revealed and paid downstairs.
    //
    // The reel still holds MYSTERY_SYMBOL_ID at those cells, though, because a
    // Golden cell is never given a new identity. So without this every one of
    // them resolved to the MYSTERY spine and played its landing and idle over
    // the composite — the room opening on a board of question marks animating
    // over the coins the player had just won.
    //
    // Marking them is the whole fix: `hasGoldenMystery` already guards all
    // seven ordinary presentation paths, so they simply skip these rows.
    //
    // ── Except the gamble scatter, which must stay a spine ──
    //
    // It fires its pips into the level box, so it cannot be a texture. The
    // effect above owns it through showSettledGambleScatter, and it is skipped
    // here for the same reason FreeSpinBasicReel skips it when painting.
    //
    // Cleared when the arrival board goes — the first spin — or the rows would
    // be refused for the rest of the round.
    useEffect(() => {
      const controller = overlayReelControllerRef.current;
      if (!controller) return undefined;

      const onTriggerBoard = freeSpinsRound?.isTriggerBoard === true;
      if (!onTriggerBoard || freeSpinsSpinning) {
        controller.clearGoldenStatic?.();
        return undefined;
      }

      const scatterRow =
        Array.isArray(triggerScatterCell) && triggerScatterCell[0] === index
          ? triggerScatterCell[1]
          : null;

      // x1, never the room's multiplier: this board was played in the BASE
      // game and paid at x1 — the same reason the reel's own paint passes 1.
      const rows = [...goldenFinalBoard(freeSpinsRound, 1).values()]
        .filter((cell) => cell.reel === index && cell.row !== scatterRow)
        .map((cell) => cell.row);

      controller.markGoldenStatic?.(rows);
      return () => controller.clearGoldenStatic?.();
    }, [
      index,
      controllerReady,
      freeSpinsRoomActive,
      freeSpinsSpinning,
      freeSpinsRound,
      triggerScatterCell,
    ]);

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - (Settings.REEL_COUNT - 1) / 2),
      [reelSpacing, index]
    );

    // ✅ Initialize controller once when component mounts
    //
    // The 3 symbol containers are rendered by FreeSpinBasicOverlayReelSet now, not here, so
    // they arrive as a prop. React attaches every ref in the commit phase
    // before any passive effect runs, and the symbols are emitted ahead of the
    // reels, so they should already be populated — but the controller calls
    // overlaySymbol.addChild() during construction, and a null there would
    // silently leave a reel with no spines. Retry on rAF instead of assuming,
    // same guard PotCollectionOverlaySymbol uses.
    useEffect(() => {
      let rafId = null;

      const tryInit = () => {
        rafId = null;
        if (overlayReelControllerRef.current) return;

        // Counted explicitly rather than with .every() — the refs arrive in a
        // `new Array(SYMBOLS_VISIBLE)`, and every()/filter() SKIP holes in a
        // sparse array, so `.every(Boolean)` reports true on an array that is
        // completely empty. That would wave through a controller with
        // undefined cells and leave the reel with no spines at all.
        let attachedCount = 0;
        for (let i = 0; i < Settings.SYMBOLS_VISIBLE; i++) {
          if (symbolRefs?.[i]) attachedCount++;
        }

        const ready =
          reelContainerRef.current &&
          attachedCount === Settings.SYMBOLS_VISIBLE;

        if (!ready) {
          rafId = requestAnimationFrame(tryInit);
          return;
        }

        safeInit(() => {
          overlayReelControllerRef.current = new FreeSpinBasicOverlayReelController(
            symbolRefs,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current,
            betRef.current ?? bet
          );
          // Published as STATE, not just the ref.
          //
          // The controller lands here after a retry loop and a safeInit — a
          // frame or more past mount, and invisible to React either way. Any
          // effect that needs it and does not list it in its deps runs once
          // against null and never again. That is what left the settled
          // scatter unmounted: its effect fires on the free-spins round, the
          // round is already in the store when the room opens, and by the time
          // this controller existed there was nothing left to re-run it.
          setControllerReady(true);
        });
      };

      tryInit();

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        setControllerReady(false);
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Handle resize of all overlay symbols
    useEffect(() => {
      if (overlayReelControllerRef.current) {
        overlayReelControllerRef.current.resizeAnimations(
          symbolWidth,
          symbolHeight
        );
      }
    }, [symbolHeight, symbolWidth]);

    // 💰 Bet update - update coin values when bet changes
    useEffect(() => {
      // Tracked unconditionally: a controller built later reads this instead of
      // the mount effect's captured bet.
      betRef.current = bet;
      if (!overlayReelControllerRef.current) return;
      overlayReelControllerRef.current.updateBet(bet);
    }, [bet]);

    // ✅ Expose all imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getOverlayReelController: () => overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showAppearAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimation(
          symbolIndex,
          symbolId
        );
      },

      showAppearAnimationAtPosition: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimationAtPosition(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      replaceVisibleSymbols: (targetSymbolId) => {
        overlayReelControllerRef.current?.replaceVisibleSymbols(targetSymbolId);
      },

      playExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReel();
      },

      playExpandingWildFullReelWin: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReelWin();
      },

      hideExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.hideExpandingWildFullReel();
      },

      forceStopExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.forceStopExpandingWildFullReel();
      },

      playExpandingWildDarkAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildDarkAnimation();
      },

      playExpandingWildWinAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildWinAnimation();
      },
    }));

    // This container no longer holds the reel's symbols — that nesting is
    // exactly what made per-symbol z-index impossible, since children can only
    // sort against siblings. Its sole remaining job is hosting the expanding
    // wild spine, which is full-reel height and so can't live in a symbol cell.
    // It keeps the same x, so the spine's reel-local coordinates
    // (position.set(0,0), scaleData.x) still mean what they always meant.
    return (
      <Container
        ref={reelContainerRef}
        name={`spine_overlay_ew_${index}`}
        x={containerX}
        zIndex={15}
      />
    );
  }
);

export default FreeSpinBasicOverlayReel;
